/**
 * Every call this module makes, in one place.
 *
 * No component fetches (coding standards 11.10). Components ask TanStack Query
 * for a key, Query calls one of these, and a mutation invalidates what it
 * touched. That is the whole caching story: there is one layer and it is not
 * spread across nine files.
 *
 * The module carries no address and no token of its own. Both are handed in by
 * the application it hangs inside, which is what lets it be copied into a
 * second application without an Entra registration, a CORS rule or a second
 * sign in (FR-19, FR-23).
 *
 * One boundary decision worth naming, because the API is being written beside
 * this in another worktree: the wire is snake_case, because that is what a
 * FastAPI response looks like without an alias generator on every model, and
 * this file turns it into the camelCase the components read. It happens here
 * and nowhere else, so there is exactly one place to change if the API ever
 * speaks camelCase itself.
 */
import { SCREENSHOT_CONTENT_TYPE, screenshotPath } from "./screenshot";
import type {
  ApplicationRef,
  Caller,
  DispositionRequest,
  HelpArticle,
  HelpGap,
  HelpSearchResult,
  NewSubmission,
  Queue,
  Roadmap,
  StoredScreenshot,
  SubmissionDetail,
  SubmissionSummary,
  SubmissionType,
  ThreadMessage,
} from "./types";

export interface ClientOptions {
  /** Where the host mounted the router, for instance "/api/feedback". */
  apiBase: string;
  /** The host's token, asked for per call so a refresh reaches us. */
  getToken: () => Promise<string> | string;
  /** Used by fetch-less tests and by nothing else. */
  fetchImpl?: typeof fetch;
}

/**
 * A failure with the server's own code on it.
 *
 * The reader never sees `code`: he gets the module's own wording. The code is
 * for whoever reads the console afterwards, and losing it is how a support
 * question becomes a guess.
 */
export class FeedbackError extends Error {
  readonly code: string;
  readonly status: number;
  readonly field?: string;

  constructor(message: string, code: string, status: number, field?: string) {
    super(message);
    this.name = "FeedbackError";
    this.code = code;
    this.status = status;
    this.field = field;
  }
}

const CAMEL = /_([a-z0-9])/g;

/** snake_case to camelCase, all the way down, arrays included. */
export function camelise<T>(value: unknown): T {
  if (Array.isArray(value)) return value.map((item) => camelise(item)) as T;
  if (value === null || typeof value !== "object") return value as T;
  const out: Record<string, unknown> = {};
  for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
    out[key.replace(CAMEL, (_, letter: string) => letter.toUpperCase())] = camelise(nested);
  }
  return out as T;
}

const SNAKE = /[A-Z]/g;

/** And back again, for what we send. */
export function snakeify(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(snakeify);
  if (value === null || typeof value !== "object") return value;
  const out: Record<string, unknown> = {};
  for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
    out[key.replace(SNAKE, (letter) => `_${letter.toLowerCase()}`)] = snakeify(nested);
  }
  return out;
}

export class FeedbackClient {
  private readonly apiBase: string;
  private readonly getToken: ClientOptions["getToken"];
  private readonly doFetch: typeof fetch;

  constructor({ apiBase, getToken, fetchImpl }: ClientOptions) {
    this.apiBase = apiBase.replace(/\/$/, "");
    this.getToken = getToken;
    this.doFetch = fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  private async request<T>(path: string, init?: { method?: string; body?: unknown }): Promise<T> {
    const token = await this.getToken();
    const hasBody = init?.body !== undefined;
    const response = await this.doFetch(`${this.apiBase}${path}`, {
      method: init?.method ?? "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        ...(hasBody ? { "Content-Type": "application/json" } : {}),
      },
      body: hasBody ? JSON.stringify(snakeify(init?.body)) : undefined,
    });

    return this.answer<T>(response);
  }

  /**
   * What a response means, whatever was sent to get it.
   *
   * Split out of `request` when the screenshot arrived, because that one call
   * sends raw bytes rather than JSON and everything after the send is the same:
   * the same envelope, the same codes, the same snake to camel pass. Two copies
   * of this would be two behaviours the day somebody changes one.
   */
  private async answer<T>(response: Response): Promise<T> {
    if (!response.ok) {
      // A failed call still has to say what went wrong. A body that is not the
      // agreed envelope is itself a finding, so it gets a code of its own
      // rather than being swallowed into a generic message.
      let detail = `HTTP ${response.status}`;
      let code = "UNEXPECTED_RESPONSE";
      let field: string | undefined;
      try {
        const body = (await response.json()) as { detail?: string; code?: string; field?: string };
        if (body.detail) detail = body.detail;
        if (body.code) code = body.code;
        field = body.field;
      } catch {
        // Left as it is: an unreadable error body is what an outage looks like.
      }
      throw new FeedbackError(detail, code, response.status, field);
    }

    if (response.status === 204) return undefined as T;
    return camelise<T>(await response.json());
  }

  /** Who the server thinks is asking, and whether it lets him triage (FR-8a). */
  me(): Promise<Caller> {
    return this.request<Caller>("/me");
  }

  types(): Promise<SubmissionType[]> {
    return this.request<SubmissionType[]>("/types");
  }

  /**
   * FR-27. Search comes before reporting, and a miss becomes a row (FR-28).
   *
   * Two calls, and that is the point rather than an accident. Searching is a
   * read: `GET /help/search` answers with what matched and writes nothing,
   * because an address a browser may prefetch and a proxy may repeat must not
   * put words on the wiki work list that nobody typed. Recording the gap is a
   * write, so it is `POST /help/searches`, and it happens only when the read
   * came back with nothing. The panel therefore searches on every settled
   * keystroke and writes at most once per search that failed.
   *
   * The second call answers in the same shape as the first, so what comes back
   * out of here is one result either way. `missRecorded` says which of the two
   * answered, which is what lets the no-result screen say the search is on the
   * list rather than hope so, and `alsoSearched` then counts this reader too.
   *
   * A refused write does not take the reading down with it, and that is what
   * the catch is for. The search is the reader's errand and the row is our
   * measurement of it. Chained as one promise, a 503 on the write rejected the
   * whole query, `results.data` came back undefined, and the panel drew neither
   * the results nor the no-result screen: somebody lost his answer because a
   * work list row could not be saved. He gets the reading instead, with
   * `missRecorded` still false, and the screen then does not tell him his words
   * are on a list they never reached.
   */
  async searchHelp(terms: string, screen: string): Promise<HelpSearchResult> {
    const query = new URLSearchParams({ terms });
    const found = await this.request<HelpSearchResult>(`/help/search?${query}`);
    if (found.articles.length > 0) return found;
    try {
      return await this.request<HelpSearchResult>("/help/searches", {
        method: "POST",
        body: { terms, screen },
      });
    } catch {
      // Swallowed here and nowhere else in this file. The reader asked to
      // search and the search worked; a gap list that lost one row is our
      // problem, and there is nothing he could do with the news.
      return found;
    }
  }

  article(slug: string): Promise<HelpArticle> {
    return this.request<HelpArticle>(`/help/articles/${encodeURIComponent(slug)}`);
  }

  /** Every published article for this application, grouped by area (FR-26a). */
  articles(): Promise<HelpArticle[]> {
    return this.request<HelpArticle[]>("/help/articles");
  }

  /** FR-26a and FR-28: the measured list of what people looked for and did not find. */
  gaps(): Promise<HelpGap[]> {
    return this.request<HelpGap[]>("/help/gaps");
  }

  /**
   * FR-29. Articles nobody has checked lately, which is an editor's list.
   *
   * The term is asked for and never assumed, on both sides of the wire. The
   * route declares `older_than_days` with no default because who reviews an
   * article and how often is Q12 and it is open; a number here would answer it
   * for every application that adopts the module. This call carried no query
   * string at all until 9 September 2026, so every render of the help page
   * answered 422 and the list stood empty for a reason nothing on the screen
   * could show.
   */
  staleArticles(olderThanDays: number): Promise<HelpArticle[]> {
    const query = new URLSearchParams({ older_than_days: String(olderThanDays) });
    return this.request<HelpArticle[]>(`/help/stale?${query}`);
  }

  /**
   * "Ask for a how-to", which is the second of the two ways out of the help.
   *
   * copy-lexicon.json settles that these are two actions and not one: "Report
   * it instead" puts something on the triage queue as ISSUE or IDEA, and this
   * one lands on the wiki gap list (FR-28). Sending both to the same endpoint
   * would merge the two destinations the lexicon separated on purpose.
   *
   * FR-28 records a miss when a search returns nothing, which is a machine
   * noticing. This is the reader saying it out loud, and it is the reason the
   * button on NoResult is not decoration.
   *
   * `void` is a statement about this side and not about the wire. The route
   * answers 201 with the terms, the screen and how many people have now asked;
   * the panel shows a screen that says the words are on the list rather than
   * the number, so it reads none of it. That decision is written down in
   * `READ_AS` in `scripts/check_response_shapes_match.py`, which is why nothing
   * compares the two shapes.
   */
  askForHowTo(terms: string, screen: string): Promise<void> {
    return this.request<void>("/help/gaps", { method: "POST", body: { terms, screen } });
  }

  /**
   * FR-2. The answer is the row and not the thread: a submission a moment old
   * has one message in it, which is the text the reporter just typed and is
   * still on his screen. He reads the thread by opening it.
   */
  submit(submission: NewSubmission): Promise<SubmissionSummary> {
    return this.request<SubmissionSummary>("/submissions", { method: "POST", body: submission });
  }

  /**
   * The picture of the page, beside the submission it is about (FR-21).
   *
   * Raw bytes and never JSON and never a form. There is no file name in this
   * request and nowhere to put one: the server builds the name out of the
   * domain, which is register item D15 rule 2 held up by the shape of the call
   * rather than by somebody remembering it.
   *
   * It goes through `this.doFetch` and asks the host for a token like every
   * other call, so a refresh reaches it. `screenshot.ts` deliberately holds the
   * path and the content type and no send function of its own: a second fetch
   * layer there would prefix the base address twice and hold a token that never
   * refreshes.
   *
   * A refusal here is not a failure of the report. The submission is written
   * and the reader has been told so by the time this runs.
   */
  async screenshot(submissionId: number, image: Blob): Promise<StoredScreenshot> {
    const token = await this.getToken();
    const response = await this.doFetch(`${this.apiBase}${screenshotPath(submissionId)}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": SCREENSHOT_CONTENT_TYPE,
      },
      body: image,
    });
    return this.answer<StoredScreenshot>(response);
  }

  /**
   * FR-6 and FR-25. `mine` is the reader's own; `application` is everything for
   * the application his token pins him to. There is no third scope here: `all`
   * belongs to the queue, which is the one surface that sees across them.
   */
  submissions(scope: "mine" | "application", typeCode?: string): Promise<SubmissionSummary[]> {
    const query = new URLSearchParams({ scope });
    if (typeCode) query.set("type_code", typeCode);
    return this.request<SubmissionSummary[]>(`/submissions?${query}`);
  }

  submission(id: number): Promise<SubmissionDetail> {
    return this.request<SubmissionDetail>(`/submissions/${id}`);
  }

  /** FR-22. Both sides may write, so this is not a triager-only route. */
  addMessage(id: number, body: string): Promise<ThreadMessage> {
    return this.request<ThreadMessage>(`/submissions/${id}/messages`, {
      method: "POST",
      body: { body },
    });
  }

  roadmap(): Promise<Roadmap> {
    return this.request<Roadmap>("/roadmap");
  }

  /** FR-8a. Behind the triager role on the server, not behind a name compared here. */
  queue(applicationCode?: string): Promise<Queue> {
    const query = new URLSearchParams();
    if (applicationCode) query.set("application_code", applicationCode);
    const suffix = query.size ? `?${query}` : "";
    return this.request<Queue>(`/queue${suffix}`);
  }

  applications(): Promise<ApplicationRef[]> {
    return this.request<ApplicationRef[]>("/applications");
  }

  /**
   * FR-8. The outcome, once. The answer carries what was stored rather than
   * what was sent, and this side reads none of it: the queue reloads, which is
   * the only thing that shows a triager the row has left the list.
   */
  setDisposition(id: number, request: DispositionRequest): Promise<void> {
    return this.request<void>(`/submissions/${id}/disposition`, {
      method: "POST",
      body: request,
    });
  }
}

/** The query keys, in one place, so a mutation can invalidate what it touched. */
export const queryKeys = {
  me: ["feedback", "me"] as const,
  types: ["feedback", "types"] as const,
  help: (terms: string, screen: string) => ["feedback", "help", terms, screen] as const,
  article: (slug: string) => ["feedback", "article", slug] as const,
  articles: ["feedback", "articles"] as const,
  gaps: ["feedback", "gaps"] as const,
  stale: (olderThanDays: number) => ["feedback", "stale", olderThanDays] as const,
  submissions: (scope: string, typeCode?: string) =>
    ["feedback", "submissions", scope, typeCode ?? null] as const,
  submission: (id: number) => ["feedback", "submission", id] as const,
  roadmap: ["feedback", "roadmap"] as const,
  queue: (applicationCode?: string) => ["feedback", "queue", applicationCode ?? null] as const,
  applications: ["feedback", "applications"] as const,
};
